import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChefHat, UtensilsCrossed, Users, CalendarHeart, MapPin, Sparkles, Clock } from "lucide-react";
import { SectionHeading } from "../components/SectionHeading";
import InstagramSection from "../components/InstagramSection";
import { IMAGES, SERVICES, WHY_US, BRAND } from "../data/content";
import api from "../lib/api";

const SERVICE_ICONS = { catering: UtensilsCrossed, mealprep: Clock, privatechef: ChefHat, events: CalendarHeart };

export default function Home() {
  const [menu, setMenu] = useState({});
  const [packages, setPackages] = useState([]);
  const [settings, setSettings] = useState({});

  useEffect(() => {
    api.get("/content/menu").then((r) => setMenu(r.data)).catch(() => {});
    api.get("/content/packages").then((r) => setPackages(r.data)).catch(() => {});
    api.get("/content/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-center">
        <img src={IMAGES.hero} alt="Elevated Jamaican-inspired plated dish" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 w-full">
          <div className="max-w-2xl animate-fade-up">
            <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-charcoal/40 px-4 py-1.5 backdrop-blur-sm">
              <MapPin className="w-3.5 h-3.5 text-gold" />
              <span className="eyebrow text-gold">Serving {BRAND.serviceArea}</span>
            </div>
            <h1 className="mt-6 text-4xl sm:text-5xl lg:text-7xl font-serif font-semibold leading-[1.05] text-white text-balance">
              Taste the Islands.<br /><span className="gold-text">Experience Platinum.</span>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-platinum leading-relaxed max-w-xl">
              Elevated Jamaican-inspired cuisine, unforgettable catering, private chef experiences, and convenient
              meal prep — crafted with flavor, passion, and care.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-3">
              <Link to="/friday-plates" data-testid="hero-friday-button"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-gold px-7 py-4 font-semibold text-charcoal hover:bg-gold-bright transition-transform hover:scale-[1.03]">
                Order Friday Plates <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/contact" data-testid="hero-quote-button"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-white/40 bg-white/5 backdrop-blur-sm px-7 py-4 font-semibold text-white hover:bg-white/15 transition-colors">
                Request a Quote
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* THREE OFFERINGS */}
      <section className="bg-charcoal-void py-16 lg:py-20" data-testid="home-offerings">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid gap-6 md:grid-cols-3">
            {[
              { key: "friday", tag: "This Week", title: "Friday Plates", Icon: CalendarHeart,
                desc: "Fresh individual plates available for Friday pickup or delivery — ordered during the week.",
                to: "/friday-plates", cta: "Order Friday Plates" },
              { key: "mealprep", tag: "Plan Ahead", title: "Meal Prep", Icon: Clock,
                desc: "Prepared multi-meal packages and custom meal-prep to make your week easier.",
                to: "/meal-prep", cta: "Explore Meal Prep" },
              { key: "catering", tag: "Events", title: "Catering & Private Chef", Icon: ChefHat,
                desc: "Taste of Platinum for weddings, celebrations, corporate events and intimate dinners.",
                to: "/contact", cta: "Request Catering" },
            ].map(({ key, tag, title, Icon, desc, to, cta }) => (
              <div key={key} data-testid={`offering-${key}`}
                className="flex flex-col rounded-2xl border border-white/10 bg-white/5 p-7 transition-colors hover:border-gold/50">
                <div className="w-12 h-12 rounded-xl bg-gold flex items-center justify-center"><Icon className="w-6 h-6 text-charcoal" /></div>
                <div className="mt-5 eyebrow text-gold">{tag}</div>
                <h3 className="mt-1 text-2xl font-serif font-semibold text-white">{title}</h3>
                <p className="mt-2 text-sm text-platinum leading-relaxed flex-1">{desc}</p>
                <Link to={to} data-testid={`offering-cta-${key}`}
                  className="mt-5 inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-semibold text-charcoal hover:bg-gold-bright w-fit">
                  {cta} <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT WE DO */}
      <section className="bg-bone py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="What We Do" title="Four Ways to Taste of Platinum"
            subtitle="From celebrations to weeknight convenience, we bring elevated island flavor wherever you need it." />
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {SERVICES.map((s, i) => {
              const Icon = SERVICE_ICONS[s.key] || UtensilsCrossed;
              return (
                <div key={s.key} data-testid={`service-card-${s.key}`}
                  className="group rounded-2xl border border-slate-200 bg-white p-7 transition-all hover:-translate-y-1 hover:shadow-xl hover:border-gold/50"
                  style={{ animationDelay: `${i * 80}ms` }}>
                  <div className="w-12 h-12 rounded-xl bg-charcoal flex items-center justify-center">
                    <Icon className="w-6 h-6 text-gold" />
                  </div>
                  <div className="mt-5 eyebrow text-crimson">{s.tag}</div>
                  <h3 className="mt-1 text-2xl font-serif font-semibold text-charcoal">{s.title}</h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.desc}</p>
                </div>
              );
            })}
          </div>
          <div className="mt-10 text-center">
            <Link to="/services" data-testid="home-explore-services"
              className="inline-flex items-center gap-2 text-charcoal font-semibold border-b-2 border-gold pb-1 hover:gap-3 transition-all">
              Explore Our Services <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* FEATURED CUISINE */}
      <section className="bg-charcoal py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading light eyebrow="Featured Cuisine" title="A Few Customer Favorites"
            subtitle="These are just a few customer favorites. Additional dishes and customized menu options are available upon request." />
          <div className="mt-14 grid gap-8 lg:grid-cols-2 items-center">
            <div className="grid grid-cols-2 gap-4">
              {[IMAGES.jerkTray, IMAGES.jerkPlatter, IMAGES.beefPatty, IMAGES.grill].map((src, i) => (
                <div key={i} className="aspect-square overflow-hidden rounded-2xl">
                  <img src={src} alt="Jamaican-inspired dish" loading="lazy" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
                </div>
              ))}
            </div>
            <div className="space-y-6">
              {Object.entries(menu).map(([cat, items]) => (
                <div key={cat} data-testid={`featured-category-${cat}`}>
                  <div className="eyebrow text-gold mb-2">{cat}</div>
                  <div className="flex flex-wrap gap-2">
                    {items.map((n) => (
                      <span key={n} className="rounded-full border border-white/15 bg-white/5 px-3.5 py-1.5 text-sm text-platinum">
                        {n}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
              <div className="rounded-2xl border border-gold/30 bg-gold/5 p-5">
                <p className="text-sm text-platinum">
                  Have something specific in mind? Our menu goes beyond the favorites listed here. Tell us what you're
                  craving when submitting your inquiry so we can provide the most accurate quote.
                </p>
                <Link to="/contact" data-testid="home-custom-menu-cta"
                  className="mt-4 inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-semibold text-charcoal hover:bg-gold-bright">
                  Request Your Custom Menu <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="bg-bone py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-2 items-center">
            <div>
              <SectionHeading eyebrow="Why Taste of Platinum" title="More Than a Meal. It's an Experience."
                subtitle="We pair authentic island flavor with polished, professional service — so every plate feels intentional." />
              <div className="mt-8 grid sm:grid-cols-2 gap-5">
                {WHY_US.map((w) => (
                  <div key={w.title} className="flex gap-3">
                    <Sparkles className="w-5 h-5 text-gold shrink-0 mt-0.5" />
                    <div>
                      <div className="font-semibold text-charcoal">{w.title}</div>
                      <div className="text-sm text-slate-600">{w.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <img src={IMAGES.brownStewChicken} alt="Brown stew chicken in a Dutch pot" loading="lazy" className="rounded-2xl object-cover h-full w-full row-span-2" />
              <img src={IMAGES.rastaPasta} alt="Jamaican rasta pasta with peppers" loading="lazy" className="rounded-2xl object-cover h-44 w-full" />
              <img src={IMAGES.dessertCake} alt="Jamaican rum cake" loading="lazy" className="rounded-2xl object-cover h-44 w-full" />
            </div>
          </div>
        </div>
      </section>

      {/* MEAL PREP PROMO */}
      <section className="relative py-20 lg:py-28 overflow-hidden">
        <img src={IMAGES.mealPrep1} alt="Meal prep containers" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-charcoal/85" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <SectionHeading light eyebrow="Weekly Meal Prep" title="Your Week Just Got More Flavorful."
              subtitle="Enjoy convenient, flavorful Jamaican-inspired meals prepared to make your week easier." />
          </div>
          <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {(packages.length ? packages : [{meals:4,name:"4 Meal Package"},{meals:8,name:"8 Meal Package"},{meals:12,name:"12 Meal Package"},{meals:16,name:"16 Meal Package"}]).map((p) => (
              <div key={p.meals} data-testid={`home-package-${p.meals}`}
                className="rounded-2xl border border-white/15 bg-white/5 backdrop-blur-sm p-6 text-center hover:border-gold/60 transition-colors">
                <div className="text-4xl font-serif font-semibold text-gold">{p.meals}</div>
                <div className="text-sm text-platinum mt-1">Meals / Week</div>
                <div className="text-xs text-platinum-dark mt-3">{p.price ? p.price : "Pricing provided at review"}</div>
              </div>
            ))}
          </div>
          <div className="mt-10">
            <Link to="/meal-prep" data-testid="home-build-order-cta"
              className="inline-flex items-center gap-2 rounded-full bg-gold px-7 py-4 font-semibold text-charcoal hover:bg-gold-bright transition-transform hover:scale-[1.03]">
              Build Your Meal Prep Order <ArrowRight className="w-4 h-4" />
            </Link>
            <p className="mt-4 text-sm text-platinum flex items-center gap-2">
              <Clock className="w-4 h-4 text-gold" />
              Order by {settings.order_deadline || "Thursday at 11:59 PM"} · Fulfillment {settings.fulfillment_days || "Sunday and Monday"}
            </p>
          </div>
        </div>
      </section>

      <InstagramSection />
    </div>
  );
}
